'use client';

import React, { useEffect, useRef } from 'react';

interface Props {
  onLoadMore: () => void;
  hasMore: boolean;
}

/**
 * Infinite-scroll sentinel for the SSR grid. By the time this can fire, every hit is already in
 * memory (the first batch from the SSR HTML, the rest from the dataset top-up), so it just reveals
 * the next batch via onLoadMore as the sentinel scrolls into view — it never fetches. A stable
 * IntersectionObserver (re-created only when `hasMore` flips) reveals ~one batch per scroll, with a
 * rootMargin so the next batch is ready slightly before the user reaches the end.
 *
 * Renders nothing once every tile is shown — and nothing while the listing is still topping up,
 * since the caller withholds `hasMore` until then. That doubles as the guard against revealing tiles
 * whose hits have not arrived: with no sentinel in the DOM there is nothing to intersect.
 */
const SsrInfiniteScroll: React.FC<Props> = ({ onLoadMore, hasMore }) => {
  const ref = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!hasMore) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMoreRef.current();
      },
      { rootMargin: '400px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore]);

  if (!hasMore) return null;
  return <div ref={ref} aria-hidden className="h-px w-full" />;
};

export default SsrInfiniteScroll;
